import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { IMAGES, SERVICES } from "@/lib/site";

export default function ServicesPage() {
    return (
        <main data-testid="services-page" className="relative z-10 pt-32 pb-24">
            {/* HEADER */}
            <section className="relative">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase">
                            ◇ 01 — Services
                        </div>
                        <h1 className="mt-4 font-display font-black text-white text-5xl md:text-7xl uppercase tracking-tighter leading-[0.92]">
                            Full Shipyard
                            <br />
                            Labor Stack.
                        </h1>
                        <p className="mt-6 max-w-2xl text-slate-300 text-lg leading-relaxed">
                            From NAVSEA-certified welders to riggers and
                            scaffolders, LMJ International deploys vetted
                            tradespeople, ready to mobilize.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* SERVICES LIST */}
            <section className="mt-20">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="border border-white/10 divide-y divide-white/10">
                        {SERVICES.map((svc, i) => (
                            <article
                                key={svc.slug}
                                data-testid={`service-row-${svc.slug}`}
                                className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 p-8 md:p-12 hover:bg-brand-surface/50 transition-colors group"
                            >
                                <div className="md:col-span-1 font-mono text-brand-blue text-sm tracking-[0.2em]">
                                    /{String(i + 1).padStart(2, "0")}
                                </div>
                                <div className="md:col-span-4">
                                    <h2 className="font-display font-black text-white text-2xl md:text-3xl uppercase tracking-tight leading-tight">
                                        {svc.title}
                                    </h2>
                                    <p className="mt-3 text-brand-blue font-mono text-xs tracking-[0.2em] uppercase">
                                        {svc.short}
                                    </p>
                                </div>
                                <div className="md:col-span-4 text-slate-300 leading-relaxed">
                                    {svc.description}
                                </div>
                                <div className="md:col-span-3">
                                    <ul className="space-y-2">
                                        {svc.bullets.map((b) => (
                                            <li
                                                key={b}
                                                className="flex items-start gap-2 text-sm text-slate-300"
                                            >
                                                <Check className="h-4 w-4 text-brand-blue mt-0.5 shrink-0" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* CERT BLOCK */}
            <section className="mt-24 border-y border-white/10 bg-brand-surface/40 py-20 md:py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-5">
                        <div className="relative aspect-square border border-white/10 overflow-hidden">
                            <img
                                src={IMAGES.structural}
                                alt="Structural steel"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-transparent to-transparent" />
                        </div>
                    </div>
                    <div className="md:col-span-7">
                        <div className="font-mono text-[11px] tracking-[0.3em] text-brand-blue uppercase">
                            ◇ Certifications
                        </div>
                        <h2 className="mt-4 font-display font-black text-white text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95]">
                            Qualified.
                            <br />
                            Documented.
                            <br />
                            On file.
                        </h2>
                        <div className="mt-8 grid grid-cols-2 gap-4">
                            {[
                                "NAVSEA Procedures",
                                "ABS Certification",
                                "MIL-SPEC Standards",
                                "CuNi Capability",
                                "Confined Space",
                                "Yard-Specific Quals",
                            ].map((c) => (
                                <div
                                    key={c}
                                    data-testid={`cert-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="border border-white/10 p-4 hover:border-brand-blue transition-colors"
                                >
                                    <div className="font-display font-bold text-white uppercase text-sm tracking-wide">
                                        {c}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="mt-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                    <h2 className="font-display font-black text-white text-4xl md:text-5xl uppercase tracking-tighter leading-[0.95] max-w-2xl">
                        Need a crew on the ground next week?
                    </h2>
                    <Link
                        to="/contact"
                        data-testid="services-cta"
                        className="group inline-flex items-center gap-3 bg-brand-blue hover:bg-[#004080] text-white font-display font-bold uppercase tracking-[0.18em] text-sm px-8 py-5 transition-colors"
                    >
                        Request a Quote
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                </div>
            </section>
        </main>
    );
}
